/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {InitialWinPresentationController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/InitialWinPresentationController";
import {Timeline, TimelineLite} from "gsap";
import {EdisonHelper} from "../../../utils/EdisonHelper";
import {EdisonGameConfig} from "../../EdisonGameConfig";
import {EdisonGameModuleConfig} from "../../EdisonGameModuleConfig";
import {EdisonSoundConfig} from "../../EdisonSoundConfig";
import {EdisonGameMode} from "../../gameMode/EdisonGameMode";
import {EdisonReelAreaView} from "../../reel/EdisonReelAreaView";
import {EdisonIBetWayWinData, EdisonParsedGameData} from "../../server/data/EdisonParsedGameData";
import {EdisonInitialWinCountUp} from "./EdisonInitialWinCountUp";

export class EdisonInitialWPController extends InitialWinPresentationController {

    private _reelsNum:number;
    private _symbolsNum:number;
    private _portraitYGap:number = -120;
    private _defaultFinishDelay:number = 2;
    private _aborted:boolean = false;

    private _winCountUp:EdisonInitialWinCountUp;
    private _symbolWinStates:boolean[][] = [[false, false, false],
        [false, false, false],
        [false, false, false],
        [false, false, false],
        [false, false, false]];
    private _belowStakeWinRestriction:boolean;

    constructor(indexes:[number, number]) {
        super(indexes);

        EventHandler.addEventListener(this, EdisonReelAreaView.SET_ORIENTATION, (event:GameEvent) => this.onOrientationChange(event.params[0]));

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._winCountUp = new EdisonInitialWinCountUp();
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(EdisonGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
    }

    protected startWinPresentation():void {
        const timeline:TimelineLite = new TimelineLite({
            paused : true,
            onComplete : () => this.finish()
        });

        this.playAllNoWinSymbols(timeline);

        this.addWinAnimationsToTimeline(timeline);

        if(this._config.hasWinCountUp && this.addWinCountUpToTimeline) {
            this.addWinCountUpToTimeline(timeline);
        }

        this._timeline = timeline;
        this._timeline.add(() => {}, this.getFinishDelayTime());
        this._timeline.play();
    }

    private getFinishDelayTime():number {
        let delay:number = this._defaultFinishDelay;

        if((<EdisonParsedGameData>this._gameData)!.randomWildModeTriggeredThisSpin)
            delay += 2;

        return delay;
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        if(isAborted) {
            SlotGame.sound.fadeAmbience(1, 300);
            this.addAllNoWinSymbolsToTimeline(new TimelineLite(), 0, 0);
        }


        if(this._gameData!.nextMode === EdisonGameMode.STACKED_RESPIN && this.hasWin()) {
            EdisonGameModuleConfig.featureController.startRespinFeature();
        }

        this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        SlotGame.winFieldController.showWinField();
    }

    protected playAllNoWinSymbols(timeline:Timeline):void {
        this._aborted = false;

        timeline.add(() => {
            if(this._gameData) {
                EdisonGameModuleConfig.winWaysSign.showWaysWon(this.calculateTotalBetwayWins(), "initial");
            }
        });

        const noWinDelay:number = .4;

        timeline.add(() => EdisonGameModuleConfig.scatterOverlay.setNoWin(), noWinDelay);
        if (!this._belowStakeWinRestriction) {
            if (this._gameData!.mode !== EdisonGameMode.FREESPIN)
                timeline.add(() => {
                    EdisonGameModuleConfig.background.changeReelBackground("winpresentation");
                }, noWinDelay);
            else
                timeline.add(() => {
                    EdisonGameModuleConfig.background.changeReelBackground("freespinWP");
                }, noWinDelay);


            EdisonGameModuleConfig.background.dimBackground(true);
        }
        this.addAllNoWinSymbolsToTimeline(timeline, 0, noWinDelay);

    }

    protected addWinAnimationsToTimeline(timeline:Timeline):void {
        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    let stacked:boolean = EdisonHelper.checkStacked(<EdisonParsedGameData>this._gameData, i);
                    timeline.add(() => {
                        if(!this._aborted)
                            EdisonGameModuleConfig.winPresentationView.playFrameOverlay(i, stacked ? 1 : j, true, stacked);
                    });
                }
            }
        }

        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    let stacked:boolean = EdisonHelper.checkStacked(<EdisonParsedGameData>this._gameData, i);
                    timeline.add(() => {
                        if(!this._aborted)
                            EdisonGameModuleConfig.winPresentationView.setFrameImage(i * 3 + j, stacked);
                    }, 1.6);
                }
            }
        }
    }

    protected parseFeatureGameData(data:EdisonParsedGameData):void {
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => false)
        });

        if(data.betWayWins!.length > 0)
            this.parseSymbolWinStates(data.betWayWins!);

        this._belowStakeWinRestriction = SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue);
    }

    private parseSymbolWinStates(betWinsArr:EdisonIBetWayWinData[]) {
        this._symbolWinStates = [[false, false, false],
            [false, false, false],
            [false, false, false],
            [false, false, false],
            [false, false, false]];

        betWinsArr.forEach((object) => {
            for(let i:number = 0; i < EdisonGameConfig.instance.REELS_NUM; i++) {
                for(let j:number = 0; j < EdisonGameConfig.instance.SYMBOLS_NUM_IN_REEL[2]; j++) {
                    if(object.reelWinPositions[i][j])
                        this._symbolWinStates[i][j] = true;
                }
            }
        });
    }

    protected hasWin():boolean {
        return this._gameData!.singleWin > 0;
    }

    private addAllNoWinSymbolsToTimeline(timeline:Timeline, duration:number, delay:number):void {
        const normalNoWinSymbols:(StateReelSymbol | null)[] = [];
        const goldNoWinSymbols:(StateReelSymbol | null)[] = [];

        this._symbolWinStates.forEach((reel, reelIndex) => {
            reel.forEach((sym, symIndex) => {
                if(!sym) {
                    if(this.checkGoldSym(reelIndex, symIndex)) {
                        goldNoWinSymbols.push(Reels.getSymbol(reelIndex, symIndex))
                    } else {
                        normalNoWinSymbols.push(Reels.getSymbol(reelIndex, symIndex))
                    }
                }
            });
        });

        normalNoWinSymbols.forEach((noWinSymbol) => {
            timeline.add(() => {
                noWinSymbol!.changeState({
                    state : EdisonGameConfig.instance.SYMBOL_STATES.noWin!,
                    fadeInDuration : duration
                })
            }, delay);
        });

        goldNoWinSymbols.forEach((noWinSymbol) => {
            timeline.add(() => {
                noWinSymbol!.changeState({
                    state : EdisonGameConfig.instance.SYMBOL_STATES.noWinFS!,
                    fadeInDuration : duration
                })
            }, delay);
        });

        (<EdisonParsedGameData>this._gameData).stackedModeOverlay.forEach(
            (overlay, index) => {
                if(overlay) {
                    if(!this.checkStackedWin(index))
                        timeline.add(() => {
                            EdisonGameModuleConfig.respinOverlay.tintOverlay(index);
                        }, delay);
                }
            }
        );

    }

    private checkGoldSym(xIndex:number, yIndex:number):boolean {
        return (<EdisonParsedGameData>this._gameData)!.evaluatedArea[xIndex][yIndex].indexOf("W") !== -1;
    }

    private calculateTotalBetwayWins():number {
        let total:number = 0;
        (<EdisonParsedGameData>this._gameData).betWayWins!.forEach((win) => {
            total += win.numberOfCombinations;
        });

        return total;
    }

    private checkStackedWin(reelIndex:number):boolean {
        let returnValue:boolean = false;
        (<EdisonParsedGameData>this._gameData).betWayWins!.forEach((win) => {
            let count:number = win.reelWinPositions[reelIndex].filter(function(elm) {return elm}).length;
            if((win.symbol === "M1" || win.symbol === "M2") && count === 3)
                returnValue = true;
        });
        return returnValue;
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0.2;
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay:number):number {
        return 0.3;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }
        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;
        const noOfFsReset:number = (<EdisonParsedGameData>this._gameData!).meterFSReset.filter(function(x) {return x}).length;
        const fsDelay:number = (<EdisonParsedGameData>this._gameData!).mode === EdisonGameMode.FREESPIN ? noOfFsReset * 1.3 : 0;

        const countUpTimeline:TimelineLite = new TimelineLite();

        if (!this._belowStakeWinRestriction){
            countUpTimeline.add(this.playWinSound());
        }

        countUpTimeline.add(this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier));
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(totalWin));

        timeline.add(countUpTimeline, fsDelay);
    }

    private playWinSound():TimelineLite {
        let tl:TimelineLite = new TimelineLite();

        if(this._gameData!.singleWin < this._gameData!.playedBetValue * EdisonGameConfig.instance.WIN_RATIOS.bigWin[0].ratio && this._gameData!.singleWin > 0) {
            tl.add(() => {
                SlotGame.sound.fadeAmbience(0, 3000);
            });
            tl.add(() => {
                SlotGame.sound.playEffect((<any>EdisonSoundConfig.instance)[`WIN_EFFECT_${(<String>this._gameData!.currentWinRatioConfig!.type).toUpperCase()}`]);
            }, .5);
            tl.add(() => {
                SlotGame.sound.fadeAmbience(1, 1000);
            }, 1);
        }
        return tl;
    }

    private onOrientationChange(data:IResizeData):void {
        if(data.orientation === Orientation.PORTRAIT) {
            this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5 + this._portraitYGap);
        } else {
            this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        }
    }

    protected finish(isAborted:boolean = false):void {
        this._aborted = true;
        if((<EdisonParsedGameData>this._gameData)!.mode !== EdisonGameMode.FREESPIN) {
            EdisonGameModuleConfig.winPresentationView.resetAnimations();
        } else if((<EdisonParsedGameData>this._gameData)!.nextMode !== EdisonGameMode.FREESPIN) {
            EdisonGameModuleConfig.freespinController.deactivateFreespinOverlay();
        }

        if((<EdisonParsedGameData>this._gameData)!.mode === EdisonGameMode.STACKED_RESPIN) {
            EdisonGameModuleConfig.background.currentMode = "normal";
        }
        super.finish(isAborted);
    }
}